/**
 * The Lines panel's virtualised tree grid (W2-07; DESIGN.md §4.10, U§5 B7): fixed-height rows, only the rows in view
 * (plus a margin and the keyboard cursor) are in the DOM, so charts with thousands of lines scroll smoothly.
 *
 * Keyboard (the grid is one tab stop; unmodified keys are handled here first, chords go to the keymap):
 *   Up / Down move (and select) · Shift+Up / Down extend the selection · Right / Left open / close a parent or go
 *   to its first child / its parent · Home / End · PageUp / PageDown · Enter makes the line active · Esc keeps only
 *   the active line selected. Delete, F2, H, Ctrl+C / X / V / A / D are key bindings of the lines commands.
 * Mouse: click selects and activates, Ctrl/Cmd+click toggles, Shift+click selects a range, double-click renames,
 * right-click opens the context menu for the selection. Drag a row onto another to make it the parent (tree view),
 * between rows to move it in the export order (tree view: next to the target, under the target's parent), below
 * the rows to remove its parent; refused drops show the reason on the row.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import type { AppServices } from '../../app/registry/types';
import type { LineId } from '../../core/model/types';
import type { MessageRef } from '../../core/util/types';
import { useT } from '../../i18n/useT';
import type { DocStore } from '../../state/types';
import { ContextMenu } from '../../ui/ContextMenu';
import { Icon } from '../../ui/Icon';
import { useTr } from '../../ui/bridge';
import { useEditorValue, useUiScale } from './hooks';
import {
  activateLine,
  lineTitle,
  reorderLines,
  renameLine,
  reparentProblemMessage,
  selectLines,
  setParentFlow,
  targetLines,
  toggleAlsoShow,
  toggleFilter,
  togglePinnedLanes,
  type FilterSet,
} from './lineActions';
import { lineContextMenu } from './lineMenu';
import { LineRow, type DropZone, type RowFlags, type RowNow } from './LineRow';
import { m } from './lines.messages';
import { inExportOrder, planDrop, rowRange, type DropPlan, type DropTarget, type LineRowModel } from './lineModel';
import { linesUi, type LinesView } from './linesUi';
import type { NowState } from './nowState';
import s from './Lines.module.css';

/** Row height in CSS px before the UI scale. */
export const ROW_HEIGHT = 26;
const OVERSCAN = 8;
/** Rows assumed visible before the first measurement (and without layout, e.g. tests). */
const FALLBACK_ROWS = 30;

/** Visible index range [first, last) for a scroll position. */
export function rowWindow(scrollTop: number, viewport: number, rowH: number, count: number): readonly [number, number] {
  const h = Math.max(1, rowH);
  const first = Math.max(0, Math.floor(scrollTop / h) - OVERSCAN);
  const last = Math.min(count, Math.ceil((scrollTop + viewport) / h) + OVERSCAN);
  return [first, Math.max(first, last)];
}

export interface LineTreeProps {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly rows: readonly LineRowModel[];
  readonly view: LinesView;
  readonly now: NowState | null;
  readonly renaming: LineId | null;
  readonly reveal: { readonly line: LineId; readonly seq: number } | null;
  /** Accessible name of the grid. */
  readonly label: string;
  readonly onToggleCollapse: (id: LineId) => void;
  /** Open the collapsed ancestors of a line (reveal); a no-op when none is collapsed. */
  readonly onExpandTo: (id: LineId) => void;
}

interface DropState {
  readonly target: DropTarget;
  readonly plan: DropPlan;
  readonly message: MessageRef | null;
}

function zoneOf(e: DragEvent<HTMLElement>, view: LinesView): DropZone {
  const rect = e.currentTarget.getBoundingClientRect();
  const y = rect.height > 0 ? (e.clientY - rect.top) / rect.height : 0.5;
  if (view === 'order') return y < 0.5 ? 'before' : 'after';
  if (y < 0.25) return 'before';
  if (y > 0.75) return 'after';
  return 'onto';
}

function sameTarget(a: DropTarget, b: DropTarget): boolean {
  if (a.kind === 'root' || b.kind === 'root') return a.kind === b.kind;
  return a.line === b.line && a.zone === b.zone;
}

export function LineTree(props: LineTreeProps) {
  const { services, store, rows, view, now, reveal, onToggleCollapse, onExpandTo } = props;
  const t = useT();
  const tr = useTr();
  const baseId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const rowH = Math.max(16, Math.round(ROW_HEIGHT * useUiScale(services)));
  const selected = useEditorValue(services, (st) => st.selection.lines);
  const active = useEditorValue(services, (st) => st.activeLine);
  const filters = useEditorValue(services, (st) => st.filters);
  const pinned = useEditorValue(services, (st) => st.pinnedLanes);
  const alsoShow = useEditorValue(services, (st) => st.alsoShow);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(0);
  // The keyboard cursor; it follows the active line whenever that changes (preview click, switcher, commands).
  const [cursorState, setCursorState] = useState<{ readonly id: LineId | null; readonly forActive: LineId | null }>({
    id: active,
    forActive: active,
  });
  const cursor = cursorState.forActive === active ? cursorState.id : active;
  const anchor = useRef<LineId | null>(null);
  const dragIds = useRef<LineId[] | null>(null);
  const handled = useRef<{ active: LineId | null; reveal: number }>({ active: null, reveal: -1 });
  const [drop, setDrop] = useState<DropState | null>(null);
  const [dragging, setDragging] = useState(false);

  const ids = useMemo(() => rows.map((r) => r.info.id), [rows]);
  const indexOf = useMemo(() => new Map(ids.map((id, i) => [id, i] as const)), [ids]);
  const rowDomId = useCallback((id: LineId): string => `${baseId}-line-${String(id)}`, [baseId]);

  const setCursor = useCallback(
    (id: LineId | null): void => {
      setCursorState({ id, forActive: services.editor.getState().activeLine });
    },
    [services],
  );

  useEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;
    const observer = new ResizeObserver(() => {
      setViewport(el.clientHeight);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  const viewportPx = viewport > 0 ? viewport : FALLBACK_ROWS * rowH;

  /** Scrolls row `i` into view (below the sticky header row); the scroll event updates the window. */
  const scrollToIndex = useCallback(
    (i: number): void => {
      const el = ref.current;
      if (el === null || i < 0) return;
      const vh = el.clientHeight > 0 ? el.clientHeight : viewportPx;
      const top = i * rowH;
      if (top < el.scrollTop) el.scrollTop = top;
      else if (top + 2 * rowH > el.scrollTop + vh) el.scrollTop = top + 2 * rowH - vh;
    },
    [rowH, viewportPx],
  );

  // Show the active line when it changes, and rows asked for by commands (new lines, palette, rename).
  useEffect(() => {
    const h = handled.current;
    if (active !== null && active !== h.active) {
      const i = indexOf.get(active);
      if (i === undefined) onExpandTo(active);
      else {
        scrollToIndex(i);
        h.active = active;
      }
    }
    if (reveal !== null && reveal.seq !== h.reveal) {
      const i = indexOf.get(reveal.line);
      if (i === undefined) onExpandTo(reveal.line);
      else {
        scrollToIndex(i);
        h.reveal = reveal.seq;
      }
    }
  }, [active, reveal, indexOf, scrollToIndex, onExpandTo]);

  // ------------------------------ selection ------------------------------

  const onPointerSelect = useCallback(
    (id: LineId, e: MouseEvent): void => {
      ref.current?.focus({ preventScroll: true });
      const toggle = e.ctrlKey || e.metaKey;
      if (e.shiftKey) {
        const st = services.editor.getState();
        selectLines(services, rowRange(rows, anchor.current ?? st.activeLine, id), id, toggle ? 'add' : 'replace');
        setCursor(id);
        return;
      }
      anchor.current = id;
      if (toggle) {
        const was = services.editor.getState().selection.lines.has(id);
        selectLines(services, [id], id, 'toggle');
        if (!was) services.editor.getState().setActiveLine(id);
      } else {
        activateLine(services, id, { select: true });
      }
      setCursor(id);
    },
    [services, rows, setCursor],
  );

  const moveCursor = (to: number, extend: boolean): void => {
    if (ids.length === 0) return;
    const i = Math.max(0, Math.min(ids.length - 1, to));
    const id = ids[i];
    if (id === undefined) return;
    scrollToIndex(i);
    if (extend) {
      selectLines(services, rowRange(rows, anchor.current ?? cursor, id), id, 'replace');
    } else {
      anchor.current = id;
      activateLine(services, id, { select: true });
    }
    setCursor(id);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.target !== e.currentTarget) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const at = cursor === null ? -1 : (indexOf.get(cursor) ?? -1);
    const page = Math.max(1, Math.floor(viewportPx / rowH) - 2);
    const row = at >= 0 ? rows[at] : undefined;
    switch (e.key) {
      case 'ArrowDown':
        moveCursor(at < 0 ? 0 : at + 1, e.shiftKey);
        break;
      case 'ArrowUp':
        moveCursor(at < 0 ? ids.length - 1 : at - 1, e.shiftKey);
        break;
      case 'Home':
        moveCursor(0, e.shiftKey);
        break;
      case 'End':
        moveCursor(ids.length - 1, e.shiftKey);
        break;
      case 'PageDown':
        moveCursor(at + page, e.shiftKey);
        break;
      case 'PageUp':
        moveCursor(at - page, e.shiftKey);
        break;
      case 'ArrowRight':
        if (row === undefined || view !== 'tree' || !row.hasChildren) return;
        if (!row.expanded) onToggleCollapse(row.info.id);
        else moveCursor(at + 1, false);
        break;
      case 'ArrowLeft': {
        if (row === undefined || view !== 'tree') return;
        if (row.hasChildren && row.expanded) onToggleCollapse(row.info.id);
        else {
          const parent = row.info.parent;
          const pi = parent === null ? undefined : indexOf.get(parent);
          if (pi === undefined) return;
          moveCursor(pi, false);
        }
        break;
      }
      case 'Enter':
        if (row === undefined) return;
        anchor.current = row.info.id;
        activateLine(services, row.info.id, { select: true });
        break;
      case 'Escape': {
        const st = services.editor.getState();
        if (st.selection.lines.size <= 1) return;
        const keep = st.activeLine;
        selectLines(services, keep === null ? [] : [keep], keep);
        break;
      }
      default:
        return;
    }
    e.preventDefault();
    e.stopPropagation();
  };

  // ------------------------------ row callbacks ------------------------------

  const onToggle = useCallback(
    (id: LineId, set: FilterSet): void => {
      // A toggle on a selected row applies to the whole selection.
      const sel = services.editor.getState().selection.lines;
      toggleFilter(services, set, sel.has(id) ? inExportOrder(store.doc, sel) : [id]);
    },
    [services, store],
  );
  const onRename = useCallback(
    (id: LineId): void => {
      if (services.editor.getState().filters.lockedLines.has(id)) {
        services.ui.toast(
          { key: m.lockedRefusal, params: { line: lineTitle(store, id), count: 1 } },
          { kind: 'warning' },
        );
        return;
      }
      linesUi(services).set({ renaming: id });
    },
    [services, store],
  );
  const onRenameDone = useCallback(
    (id: LineId, name: string | null): void => {
      linesUi(services).set({ renaming: null });
      if (name !== null) renameLine(services, id, name);
      ref.current?.focus({ preventScroll: true });
    },
    [services],
  );
  const onUnpin = useCallback(
    (id: LineId): void => {
      togglePinnedLanes(services, [id]);
    },
    [services],
  );
  const onStopAlsoShow = useCallback(
    (id: LineId): void => {
      toggleAlsoShow(services, [id]);
    },
    [services],
  );
  const onContextMenu = useCallback(
    (id: LineId): void => {
      if (!services.editor.getState().selection.lines.has(id)) {
        anchor.current = id;
        activateLine(services, id, { select: true });
      }
      setCursor(id);
    },
    [services, setCursor],
  );

  // ------------------------------ drag and drop ------------------------------

  const execute = useCallback(
    (plan: DropPlan, moving: readonly LineId[]): void => {
      if (!plan.ok) {
        const msg = reparentProblemMessage(store, plan.problem);
        if (msg !== null) services.ui.toast(msg, { kind: 'warning' });
        return;
      }
      if (plan.parent !== undefined) void setParentFlow(services, moving, plan.parent, plan.order);
      else if (plan.order !== null) reorderLines(services, plan.order, moving);
    },
    [services, store],
  );

  const onDragStart = useCallback(
    (id: LineId, e: DragEvent<HTMLDivElement>): void => {
      const sel = services.editor.getState().selection.lines;
      const moving = sel.has(id) ? inExportOrder(store.doc, sel) : [id];
      if (!sel.has(id)) activateLine(services, id, { select: true });
      dragIds.current = moving;
      setDragging(true);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', moving.map((l) => lineTitle(store, l)).join('\n'));
    },
    [services, store],
  );

  const updateDrop = useCallback(
    (target: DropTarget, e: DragEvent<HTMLElement>): void => {
      const moving = dragIds.current;
      if (moving === null) return;
      const plan = planDrop(store.doc, moving, target, view);
      if (plan.ok) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      } else {
        e.dataTransfer.dropEffect = 'none';
      }
      const message = plan.ok ? null : reparentProblemMessage(store, plan.problem);
      setDrop((cur) =>
        cur !== null && sameTarget(cur.target, target) && cur.plan.ok === plan.ok ? cur : { target, plan, message },
      );
    },
    [store, view],
  );

  const onDragOver = useCallback(
    (id: LineId, e: DragEvent<HTMLDivElement>): void => {
      updateDrop({ kind: 'row', line: id, zone: zoneOf(e, view) }, e);
    },
    [updateDrop, view],
  );

  const finishDrag = useCallback((): void => {
    dragIds.current = null;
    setDrop(null);
    setDragging(false);
  }, []);

  const dropOn = useCallback(
    (target: DropTarget, e: DragEvent<HTMLElement>): void => {
      const moving = dragIds.current;
      e.preventDefault();
      finishDrag();
      if (moving !== null) execute(planDrop(store.doc, moving, target, view), moving);
    },
    [store, view, execute, finishDrag],
  );

  const onDrop = useCallback(
    (id: LineId, e: DragEvent<HTMLDivElement>): void => {
      dropOn({ kind: 'row', line: id, zone: zoneOf(e, view) }, e);
    },
    [dropOn, view],
  );

  // ------------------------------ render ------------------------------

  const [first, last] = rowWindow(Math.max(0, scrollTop - rowH), viewportPx, rowH, rows.length);
  const indices: number[] = [];
  for (let i = first; i < last; i++) indices.push(i);
  const ci = cursor === null ? undefined : indexOf.get(cursor);
  if (ci !== undefined && (ci < first || ci >= last)) indices.push(ci);

  const solo = filters.soloLines;
  const nowOf = (id: LineId): RowNow | null =>
    now === null ? null : { animating: now.animating.has(id), hidden: now.hidden.get(id) ?? null };

  const menuItems = lineContextMenu(services, t, targetLines(services));
  const dropMessage = drop?.message ?? null;
  const dropRow = drop !== null && drop.target.kind === 'row' ? drop.target : null;

  return (
    <div className={s.treeWrap}>
      <ContextMenu items={menuItems} label={t(m.contextLabel)}>
        <div
          ref={ref}
          role="treegrid"
          tabIndex={0}
          aria-label={props.label}
          aria-multiselectable={true}
          aria-rowcount={rows.length + 1}
          aria-activedescendant={ci === undefined || cursor === null ? undefined : rowDomId(cursor)}
          className={s.tree}
          onKeyDown={onKeyDown}
          onScroll={(e) => {
            setScrollTop(e.currentTarget.scrollTop);
          }}
        >
          <div role="row" className={s.headRow} aria-rowindex={1}>
            <span role="columnheader">
              <span className={s.visuallyHidden}>{t(m.colToggles)}</span>
            </span>
            <span role="columnheader" className={s.index} title={t(m.colIndexHelp)}>
              {t(m.colIndex)}
            </span>
            <span role="columnheader">{t(m.colName)}</span>
            <span role="columnheader" className={s.colType}>
              {t(m.colType)}
            </span>
            <span role="columnheader" className={s.num}>
              {t(m.colNotes)}
            </span>
            <span role="columnheader" className={s.colNow} title={t(m.colNowHelp)}>
              {t(m.colNow)}
            </span>
            <span role="columnheader" className={`${s.num} ${s.colZ}`} title={t(m.colZHelp)}>
              {t(m.colZ)}
            </span>
            <span role="columnheader" title={t(m.colGroup)}>
              <span className={s.colGroupText}>{t(m.colGroup)}</span>
              <span className={s.colGroupIcon} aria-hidden>
                <Icon name="Tags" size={12} />
              </span>
            </span>
          </div>
          <div className={s.body} style={{ height: rows.length * rowH }} role="presentation">
            {indices.map((i) => {
              const row = rows[i] as LineRowModel;
              const id = row.info.id;
              const flags: RowFlags = {
                selected: selected.has(id),
                active: active === id,
                cursor: cursor === id,
                hidden: filters.hiddenLines.has(id),
                solo: solo.has(id),
                soloedOut: solo.size > 0 && !solo.has(id),
                locked: filters.lockedLines.has(id),
                focusHitsound: filters.focusHitsoundLines.has(id),
                pinned: pinned.includes(id),
                alsoShown: alsoShow.includes(id),
              };
              const rowDrop =
                dropRow !== null && drop !== null && dropRow.line === id && (drop.plan.ok || drop.message !== null)
                  ? { zone: dropRow.zone, refused: !drop.plan.ok }
                  : null;
              return (
                <LineRow
                  key={id}
                  row={row}
                  domId={rowDomId(id)}
                  top={i * rowH}
                  height={rowH}
                  flags={flags}
                  now={nowOf(id)}
                  renaming={props.renaming === id}
                  drop={rowDrop}
                  draggable={true}
                  onPointerSelect={onPointerSelect}
                  onToggle={onToggle}
                  onExpand={onToggleCollapse}
                  onRename={onRename}
                  onRenameDone={onRenameDone}
                  onUnpin={onUnpin}
                  onStopAlsoShow={onStopAlsoShow}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  onDragEnd={finishDrag}
                  onContextMenu={onContextMenu}
                />
              );
            })}
            {dropRow !== null && dropMessage !== null && (
              <div className={s.dropNote} style={{ top: ((indexOf.get(dropRow.line) ?? 0) + 1) * rowH + 2 }}>
                {tr(dropMessage)}
              </div>
            )}
          </div>
        </div>
      </ContextMenu>
      {dragging && view === 'tree' && (
        <div
          className={s.rootDrop}
          data-drop={drop?.target.kind === 'root' && drop.plan.ok ? 'root' : undefined}
          onDragOver={(e) => {
            updateDrop({ kind: 'root' }, e);
          }}
          onDrop={(e) => {
            dropOn({ kind: 'root' }, e);
          }}
        >
          {t(m.dropRoot)}
        </div>
      )}
      <span className={s.visuallyHidden} role="status" aria-live="polite">
        {dragging && dropMessage !== null ? tr(dropMessage) : ''}
      </span>
    </div>
  );
}
